import { type DatabasePermissions } from './types.ts';

/**
 * The raw inputs that feed permission resolution.
 * Maps 1:1 to what is already read from Redux, plugins, and URL today.
 */
export interface PermissionInputs {
  /**
   * Whether the user has permission to update database metadata (fields, views, etc).
   */
  canUpdateMetadata: boolean;
  /**
   * Whether the user has permission to update database entries (rows).
   */
  canUpdateEntries: boolean;
  /**
   * Whether the user has permission to delete the database.
   */
  canDelete: boolean;
  /**
   * Whether the database is locked for editing.
   */
  isLocked: boolean;
  /**
   * Whether the user is viewing an older version of the database.
   */
  isViewingVersion: boolean;
  /**
   * Whether the edit-content plugin is currently enabled.
   */
  isEditEnabled: boolean;
}

/**
 * Row-level editing: can the user add, edit, or delete rows?
 * Note: a locked database still allows content edits — only field changes are blocked.
 */
function canEditContent(inputs: PermissionInputs): boolean {
  // User must have the entries operation from the backend
  if (!inputs.canUpdateEntries) {
    return false;
  }

  // The edit-content plugin must be active (e.g. not in read-only embed mode)
  if (!inputs.isEditEnabled) {
    return false;
  }

  // Version history is read-only — no edits allowed
  if (inputs.isViewingVersion) {
    return false;
  }

  return true;
}

/**
 * Field-level editing: can the user add, remove, rename columns, or change types?
 * Locking is specifically designed to prevent structural changes while still allowing row edits.
 */
function canEditFields(inputs: PermissionInputs): boolean {
  // User must have the metadata operation from the backend
  if (!inputs.canUpdateMetadata) {
    return false;
  }

  // The edit-content plugin must be active
  if (!inputs.isEditEnabled) {
    return false;
  }

  // Version history is read-only
  if (inputs.isViewingVersion) {
    return false;
  }

  // Locked databases allow row edits but not structural changes
  if (inputs.isLocked) {
    return false;
  }

  return true;
}

/**
 * Can the user delete the database?
 * The edit plugin state does not affect deletion — a user with delete
 * permission can still delete even if the edit plugin is disabled.
 */
function canDelete(inputs: PermissionInputs): boolean {
  // User must have the delete operation from the backend
  if (!inputs.canDelete) {
    return false;
  }

  // Version history is read-only — cannot delete while viewing a past version
  if (inputs.isViewingVersion) {
    return false;
  }

  return true;
}

/**
 * Pure function. Zero dependencies.
 *
 * Single source of truth for all permission resolution logic.
 * Both ReduxDatabaseAdapter and DatabasePermissionsManager delegate here.
 */
export function resolvePermissions(inputs: PermissionInputs): DatabasePermissions {
  return {
    canEditContent: canEditContent(inputs),
    canEditFields: canEditFields(inputs),
    canDelete: canDelete(inputs),
    isLocked: inputs.isLocked,
    isViewingVersion: inputs.isViewingVersion
  };
}
